import React, { ReactElement, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';

import { useRouteMatch } from 'react-router-dom';
import {
	AnalyticsPageCategory,
	AnalyticsPageType,
	PagingOptions
} from '../../../../../models';
import {
	history,
	Asset,
	AppState,
	AppThunkDispatch,
	AssetActions,
	fetchAssetList,
	deleteAsset,
	changeAssetPage
} from '../../../../../redux';
import {
	PageContainer,
	CrudTableHeader,
	CrudDeleteDialog,
	ContentManagerOverviewHeader,
	CoMaPagingBar
} from '../../../../molecules';
import { usePageTracking } from '../../../../../hooks';
import { useTranslation } from 'react-i18next';

export interface IAssetManagerOverviewProps {
	assets: Asset[];
	pagingOptions: PagingOptions;
	fetchAssets: () => void;
	deleteAsset: (id: string) => void;
	changePage: (page: number) => void;
}

function AssetManagerOverviewPageView(
	props: IAssetManagerOverviewProps
): ReactElement {
	const { assets, pagingOptions, fetchAssets, deleteAsset, changePage } =
		props;

	usePageTracking(
		AnalyticsPageCategory.ContentManager,
		AnalyticsPageType.Asset
	);

	const { t } = useTranslation('contentManager');
	const { url } = useRouteMatch();

	const [dialogOpen, setDialogOpen] = useState<boolean>(false);
	const [idToRemove, setIdToRemove] = useState<string | undefined>(undefined);

	useEffect(() => {
		if (!url.endsWith(`/assets`)) {
			history.push(`${url}/assets`);
		}
		fetchAssets();
	}, [fetchAssets]);

	function onCreateButtonClicked() {
		history.push(`${url}/create`);
	}

	function editAsset(id: string): void {
		history.push(`${url}/${id}`);
	}

	function onPageChange(page: number): void {
		if (
			page < 0 ||
			page >= pagingOptions.totalPages ||
			page === pagingOptions.currentPage
		) {
			return;
		}
		changePage(page);
	}

	function onDeleteButtonClicked(id: string) {
		setDialogOpen(true);
		setIdToRemove(id);
	}

	function onDialogClose(): void {
		setDialogOpen(false);
		setIdToRemove(undefined);
	}

	function onDeleteConfirm(): void {
		if (idToRemove) deleteAsset(idToRemove);
		onDialogClose();
	}

	return (
		<PageContainer id="AssetManagerOverview" noPadding>
			<ContentManagerOverviewHeader
				onCreateButtonClicked={onCreateButtonClicked}
			/>
			{pagingOptions.totalPages > 1 && (
				<CoMaPagingBar
					total={pagingOptions.totalPages}
					pageIndex={pagingOptions.currentPage}
					onPageChange={onPageChange}
				/>
			)}
			<TableContainer>
				<Table>
					<CrudTableHeader
						headers={[
							{
								key: 'assetName',
								label: t('assets.table.assetName'),
								enableFiltering: true
							},
							{
								key: 'licences',
								label: t('assets.table.licences'),
								disableSort: true
							}
						]}
						onChange={fetchAssets}
					/>
					<TableBody>
						{assets.map((asset: Asset, index: number) => (
							<TableRow key={index}>
								<TableCell>{asset.assetName}</TableCell>
								<TableCell>{asset.licences}</TableCell>
								<TableCell padding="checkbox">
									<IconButton
										onClick={() =>
											editAsset(asset.id as string)
										}
									>
										<EditIcon />
									</IconButton>
								</TableCell>
								<TableCell padding="checkbox">
									<IconButton
										onClick={() =>
											onDeleteButtonClicked(
												asset.id as string
											)
										}
									>
										<DeleteIcon />
									</IconButton>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			{pagingOptions.totalPages > 1 && (
				<CoMaPagingBar
					total={pagingOptions.totalPages}
					pageIndex={pagingOptions.currentPage}
					onPageChange={onPageChange}
				/>
			)}
			<CrudDeleteDialog
				open={dialogOpen}
				onClose={onDialogClose}
				possibleCascade
				onConfirm={onDeleteConfirm}
			/>
		</PageContainer>
	);
}

function mapStateToProps(state: AppState) {
	return {
		assets: state.assets.assets,
		pagingOptions: state.assets.pagingOptions
	};
}

function mapDispatchToProps(dispatch: AppThunkDispatch<AssetActions>) {
	return {
		fetchAssets: () => dispatch(fetchAssetList()),
		deleteAsset: (id: string) => dispatch(deleteAsset(id)),
		changePage: (page: number) => dispatch(changeAssetPage(page))
	};
}

export const AssetManagerOverviewPage = connect(
	mapStateToProps,
	mapDispatchToProps
)(AssetManagerOverviewPageView);
